/**
 * This code adds AI content generation feature in WordPress.
 * @version 1.0.0
 */

// use strict mode for improved security and error handling
"use strict";

// import necessary functions and components from WordPress libraries
import { addFilter } from "@wordpress/hooks";
import { compose, createHigherOrderComponent } from "@wordpress/compose";
import { Fragment, useState } from "@wordpress/element";
import {
  PanelBody,
  ToggleControl,
  SelectControl,
  Button,
  RadioControl,
} from "@wordpress/components";
import { InspectorControls } from "@wordpress/blockEditor";

 // create a higher order component that adds the AI content generation feature to the BlockEdit component 
const wpaikitControls = createHigherOrderComponent((BlockEdit) => {
  // return a function that takes in props and returns the wrapped BlockEdit component
  return (props) => {
    // destructure necessary props and state variables
    const { attributes, setAttributes, isSelected } = props;
    const [isRunning, setIsRunning] = useState(false);
    const [aiElementType, setAiElementType] = useState("paragraph");
    const [aiBlockAction, setAiBlockAction] = useState("create");
    const [aiTask, setAiTask] = useState(Object.keys(aicontent.prompts)[0]);

    // define an array of allowed block types
    const allowedTags = [
      "core/quote",
      "core/heading",
      "core/paragraph",
      "core/preformatted",
      "core/pullquote",
      "core/verse",
      "core/list"
    ];
    
    /**
     * A function that prepares content for insertion or update based on selected block and settings.
     * @param {*} content - The generated AI content.
     * @param {string} aiElementType - The AI element type to insert.
     * @param {string} aiBlockAction - The AI block action to perform, create or update.
     * return The created or updated block.
     */
    const prepareContent = (
      content,
      aiElementType = "paragraph",
      aiBlockAction = "create"
    ) => {
      // get the client ID of the selected block
      const selectedBlockClientIds = wp.data
        .select("core/block-editor")
        .getSelectedBlockClientId();
      // get the index of the selected block
      const indexToInsertAt =
        wp.data
          .select("core/block-editor")
          .getBlockIndex(selectedBlockClientIds) + 1;
      // determine the block type based on selected element type
      let blockType;
      switch (aiElementType) {
        case "heading":
          blockType = "core/heading";
          break;
        default:
          blockType = "core/paragraph";
      }
      let blockToInsert;
      // if updating an existing block, update the attributes
      if (aiBlockAction === "update") {
        const currentBlock = wp.data
          .select("core/block-editor")
          .getSelectedBlock();
        if (currentBlock) {
          wp.data
            .dispatch("core/block-editor")
            .updateBlockAttributes(selectedBlockClientIds, {
              content: content,
            });
        }
      } else {
        // if creating a new block, create a new block object and insert it at the selected index
        
        blockToInsert = wp.blocks.createBlock(blockType, { content: content });
        
        wp.data
          .dispatch("core/block-editor")
          .insertBlock(blockToInsert, indexToInsertAt);
      }
    };

    const handleClick = (event) => {
      setIsRunning(true);
      const nonce = aicontent.nonce;
      const rest_nonce = aicontent.rest_nonce;

      let text = "";
      if ("content" in attributes) {
        text = attributes.content;
      } else if ("citation" in attributes) {
        text = attributes.citation;
      } else if ("value" in attributes) {
        text = attributes.value;
      } else if ("values" in attributes) {
        text = attributes.values;
      } else if ("text" in attributes) {
        text = attributes.text;
      }

      const response = fetch(aicontent.siteUrl + "/wp-json/aicontent/v1/text", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": rest_nonce,
        },
        body: JSON.stringify({
          text: text,
          type: aiTask,
        }),
      })
        .then(function (response) {
          // The response is a Response instance.
          // You parse the data into a useable format using `.json()`
          return response.json();
        })
        .then(function (data) {
          // `data` is the parsed version of the JSON returned from the above endpoint.
          if (
            typeof data.message != undefined &&
            typeof data.message != undefined
          ) {
            var newContent = data.message;
            prepareContent(newContent, aiElementType, aiBlockAction);
          }
          setIsRunning(false);
        });
    };

    // create an array of options for the select control based on the prompts defined in aicontent
    const promptOptions = Object.entries(aicontent.prompts).map(
      ([key, value]) => ({ label: value, value: key })
    );

    return (
      <Fragment>
        <BlockEdit {...props} />
        {isSelected && allowedTags.includes(props.name) && (
          <InspectorControls>
            <PanelBody title={wp.i18n.__("Text Prompter", "wp-ai-kit")}>
              <SelectControl
                label={wp.i18n.__("Choose Prompt", "wp-ai-kit")}
                value={aiTask}
                options={promptOptions}
                onChange={(newAiTask) => setAiTask(newAiTask)}
              />
              <SelectControl
                label={wp.i18n.__("Choose Element Type", "wp-ai-kit")}
                value={aiElementType}
                options={[
                  { label: "Paragraph", value: "paragraph" },
                  { label: "Heading", value: "heading" },
                ]}
                onChange={(newAiElementType) =>
                  setAiElementType(newAiElementType)
                }
              />
              <RadioControl
                label={wp.i18n.__("Choose Block Action", "wp-ai-kit")}
                selected={aiBlockAction}
                options={[
                  { label: "Update Existing Block", value: "update" },
                  { label: "Create New Block", value: "create" },
                ]}
                onChange={(newAiBlockAction) =>
                  setAiBlockAction(newAiBlockAction)
                }
              />
              <Button
                isBusy={isRunning}
                disabled={isRunning}
                variant="primary"
                onClick={handleClick}
              >
                {wp.i18n.__("Submit", "wp-ai-kit")}
              </Button>
            </PanelBody>
          </InspectorControls>
        )}
      </Fragment>
    );
  };
}, "wpaikitControls");

// add the AI content generation feature in WordPress
addFilter("editor.BlockEdit", "wpaikit/wp-aikit-controls", wpaikitControls);